import React from "react";
import {
    Paper,
    TextField,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    Box,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TableSortLabel,
    IconButton,
} from "@mui/material";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
} from "@mui/icons-material";
import PageContainer from "../layout/PageContainer";
import useCategories from "../../hooks/useCategories";

// ---- Sort utils ----
function getCellValue(row, key) {
    switch (key) {
        case "id":
            return row.id ?? 0;
        case "name":
            return row.name ?? "";
        case "label_de":
            return row.label_de ?? "";
        case "label_en":
            return row.label_en ?? "";
        case "label_fr":
            return row.label_fr ?? "";
        case "label_it":
            return row.label_it ?? "";
        default:
            return "";
    }
}

function descendingComparator(a, b, orderBy) {
    const va = getCellValue(a, orderBy);
    const vb = getCellValue(b, orderBy);

    const na = typeof va === "number" ? va : Number(va);
    const nb = typeof vb === "number" ? vb : Number(vb);

    if (Number.isFinite(na) && Number.isFinite(nb)) {
        return nb - na;
    }

    return (vb ?? "").toString().localeCompare((va ?? "").toString(), "de", {
        sensitivity: "base",
    });
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    return [...array]
        .map((el, idx) => [el, idx])
        .sort((a, b) => comparator(a[0], b[0]) || a[1] - b[1])
        .map((el) => el[0]);
}

function cleanOpt(v) {
    const s = (v || "").trim();
    return s ? s : null;
}

export default function CategoriesPage() {
    const {
        categories,
        loading,
        error,
        fetchCategories,
        createCategory,
        updateCategory,
        deleteCategory,
    } = useCategories();

    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("label_de");

    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState({
        name: "",
        label_de: "",
        label_en: "",
        label_fr: "",
        label_it: "",
    });
    const [editingCategory, setEditingCategory] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState("");

    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [deleteError, setDeleteError] = React.useState("");
    const [deleting, setDeleting] = React.useState(false);

    const handleOpenCreate = () => {
        setEditingCategory(null);
        setForm({
            name: "",
            label_de: "",
            label_en: "",
            label_fr: "",
            label_it: "",
        });
        setSubmitError("");
        setOpen(true);
    };

    const handleOpenEdit = (cat) => {
        setEditingCategory(cat);
        setForm({
            name: cat.name || "",
            label_de: cat.label_de || "",
            label_en: cat.label_en || "",
            label_fr: cat.label_fr || "",
            label_it: cat.label_it || "",
        });
        setSubmitError("");
        setOpen(true);
    };

    const handleSave = async () => {
        setSubmitError("");

        const name = (form.name || "").trim();
        const label_de = (form.label_de || "").trim();

        if (!name) {
            setSubmitError("Key/Name ist Pflichtfeld.");
            return;
        }
        if (!label_de) {
            setSubmitError("Label (DE) ist Pflichtfeld.");
            return;
        }

        const payload = {
            name,
            label_de,
            label_en: cleanOpt(form.label_en),
            label_fr: cleanOpt(form.label_fr),
            label_it: cleanOpt(form.label_it),
        };

        setSubmitting(true);
        try {
            if (editingCategory) {
                await updateCategory(editingCategory.id, payload);
            } else {
                await createCategory(payload);
            }
            setOpen(false);
        } catch (e) {
            setSubmitError(e.message || "Speichern fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    const rows = React.useMemo(
        () => stableSort(categories || [], getComparator(order, orderBy)),
        [categories, order, orderBy]
    );

    const columns = [
        { id: "id", label: "ID" },
        { id: "label_de", label: "Label (DE)" },
    ];

    return (
        <PageContainer
            title="Kategorien"
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={fetchCategories} disabled={loading}>
                        Aktualisieren
                    </Button>
                    <Button variant="contained" onClick={handleOpenCreate}>
                        Neue Kategorie
                    </Button>
                </Box>
            }
        >
            {error && <Alert severity="error">{error}</Alert>}

            <TableContainer component={Paper}>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map((c) => (
                                    <TableCell key={c.id}>
                                        <TableSortLabel
                                            active={orderBy === c.id}
                                            direction={order}
                                            onClick={() => {
                                                const isAsc = orderBy === c.id && order === "asc";
                                                setOrder(isAsc ? "desc" : "asc");
                                                setOrderBy(c.id);
                                            }}
                                        >
                                            {c.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((c) => (
                                <TableRow key={c.id} hover>
                                    <TableCell>{c.id}</TableCell>
                                    <TableCell>{c.label_de || "-"}</TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => handleOpenEdit(c)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            onClick={() => {
                                                setDeleteTarget(c);
                                                setDeleteDialogOpen(true);
                                                setDeleteError("");
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>
                    {editingCategory ? "Kategorie bearbeiten" : "Neue Kategorie anlegen"}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Key/Name* (technisch)"
                            helperText="z.B. it_services, handwerk, gastronomie"
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        />
                        <TextField
                            fullWidth
                            label="Label (DE)*"
                            value={form.label_de}
                            onChange={(e) => setForm((f) => ({ ...f, label_de: e.target.value }))}
                        />
                        <TextField
                            fullWidth
                            label="Label (EN)"
                            value={form.label_en}
                            onChange={(e) => setForm((f) => ({ ...f, label_en: e.target.value }))}
                        />
                        <TextField
                            fullWidth
                            label="Label (FR)"
                            value={form.label_fr}
                            onChange={(e) => setForm((f) => ({ ...f, label_fr: e.target.value }))}
                        />
                        <TextField
                            fullWidth
                            label="Label (IT)"
                            value={form.label_it}
                            onChange={(e) => setForm((f) => ({ ...f, label_it: e.target.value }))}
                        />
                    </Box>

                    {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave} disabled={submitting}>
                        {submitting ? "Speichern…" : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => !deleting && setDeleteDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Kategorie löschen</DialogTitle>
                <DialogContent dividers>
                    {deleteError ? (
                        <Alert severity="error">{deleteError}</Alert>
                    ) : (
                        <Alert severity="warning">
                            Willst du die Kategorie <b>{deleteTarget?.label_de || deleteTarget?.name}</b> wirklich löschen?
                            <br />
                            Zuordnungen zu Firmen werden automatisch entfernt.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                        Abbrechen
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        onClick={async () => {
                            if (!deleteTarget) return;
                            setDeleting(true);
                            setDeleteError("");
                            try {
                                await deleteCategory(deleteTarget.id);
                                setDeleteDialogOpen(false);
                            } catch (e) {
                                setDeleteError(e.message || "Löschen fehlgeschlagen.");
                            } finally {
                                setDeleting(false);
                            }
                        }}
                    >
                        {deleting ? "Löschen…" : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
